import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface TATConfiguration {
  id: string;
  workflow_step?: string;
  from_step?: string;
  to_step?: string;
  allowed_tat_minutes: number;
  level_1_threshold_percentage: number;
  level_2_threshold_percentage: number;
  grace_period_minutes: number;
  is_active: boolean;
}

export const useTATConfigurations = () => {
  const queryClient = useQueryClient();
  
  const query = useQuery({
    queryKey: ["tat-configurations"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tat_configurations')
        .select('*')
        .eq('is_active', true)
        .not('workflow_step', 'is', null);
      
      if (error) throw error;
      return data as TATConfiguration[];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  const updateTATConfiguration = useMutation({
    mutationFn: async (config: {
      id: string;
      allowed_tat_minutes: number;
      level_1_threshold_percentage: number;
      level_2_threshold_percentage: number;
      grace_period_minutes: number;
    }) => {
      const { data, error } = await supabase
        .from('tat_configurations')
        .update({
          allowed_tat_minutes: config.allowed_tat_minutes,
          level_1_threshold_percentage: config.level_1_threshold_percentage,
          level_2_threshold_percentage: config.level_2_threshold_percentage,
          grace_period_minutes: config.grace_period_minutes,
          updated_at: new Date().toISOString()
        })
        .eq('id', config.id);
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["tat-configurations"] });
    }
  });

  return { ...query, updateTATConfiguration };
};
